import { useState } from "react";
import { Plus, Trash2, ChevronDown, Ruler, Home, Package, Eye, EyeOff } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { sqft } from "@/lib/quotationApi";
import RoomImages from "./RoomImages";

const AREA_TYPES = ["Kitchen", "Bedroom", "Living Room", "Wardrobe", "Study Room", "Pooja Unit", "Other"];

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function MeasurementBlock({ m, finishes, onChange, onDelete, index, modularMultiplier }) {
  const selectedFinish = finishes.find((f) => f.id === m.finish_id);
  const areaVal = sqft(m);
  const basePrice = selectedFinish ? selectedFinish.price_per_sqft : 0;
  const modMult = Math.max(0, parseFloat(modularMultiplier) || 0);
  const effectivePrice = m.is_modular ? basePrice * modMult : basePrice;
  const cost = areaVal * effectivePrice;

  // Support legacy feet values: if length_in absent but length_ft present, surface it in inches.
  const shownLength =
    m.length_in ?? (m.length_ft !== undefined && m.length_ft !== "" ? (parseFloat(m.length_ft) || 0) * 12 : "");
  const shownHeight =
    m.height_in ?? (m.height_ft !== undefined && m.height_ft !== "" ? (parseFloat(m.height_ft) || 0) * 12 : "");

  const updateDim = (key, val) => {
    // Keep legacy *_ft mirrored so any old code or PDF fallback still reads correctly.
    const num = parseFloat(val);
    const ftKey = key === "length_in" ? "length_ft" : "height_ft";
    const ftVal = Number.isFinite(num) ? num / 12 : "";
    onChange({ ...m, [key]: val, [ftKey]: ftVal });
  };

  const included = m.is_included !== false;

  return (
    <div
      className={`bg-white/[0.02] border rounded-xl p-4 mb-3 relative transition ${
        included ? "border-white/5" : "border-red-500/20 opacity-60"
      }`}
      data-testid={`measurement-block-${index}`}
    >
      <div className="absolute top-2.5 right-2.5 flex items-center gap-1">
        <button
          type="button"
          className={`transition p-1 ${included ? "text-[#C5A880]" : "text-white/30 hover:text-white/60"}`}
          onClick={() => onChange({ ...m, is_included: !included })}
          data-testid={`measurement-toggle-${index}`}
          aria-label={included ? "Exclude from quotation" : "Include in quotation"}
          title={included ? "Exclude from quotation" : "Include in quotation"}
        >
          {included ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
        <button
          type="button"
          className="text-white/30 hover:text-red-400 transition p-1"
          onClick={onDelete}
          data-testid={`measurement-delete-${index}`}
          aria-label="Delete"
        >
          <Trash2 size={16} />
        </button>
      </div>

      <div className="overline text-[#C5A880]/80 mb-3 flex items-center gap-2">
        <span className={included ? "" : "line-through"}>Measurement {index + 1}</span>
        {!included && (
          <span className="text-red-400/80 text-[10px] not-italic normal-case tracking-normal font-medium">
            Excluded
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="label-luxe">Length (in)</label>
          <input
            type="number"
            className="input-luxe"
            placeholder="0"
            inputMode="decimal"
            value={shownLength ?? ""}
            onChange={(e) => updateDim("length_in", e.target.value)}
            data-testid={`measurement-length-${index}`}
          />
        </div>
        <div>
          <label className="label-luxe">Height (in)</label>
          <input
            type="number"
            className="input-luxe"
            placeholder="0"
            inputMode="decimal"
            value={shownHeight ?? ""}
            onChange={(e) => updateDim("height_in", e.target.value)}
            data-testid={`measurement-height-${index}`}
          />
        </div>
      </div>

      <div className="mb-3">
        <label className="label-luxe">Finish</label>
        <Select
          value={m.finish_id || ""}
          onValueChange={(v) => {
            const f = finishes.find((x) => x.id === v);
            onChange({
              ...m,
              finish_id: v,
              finish_name: f?.name,
              finish_price: f?.price_per_sqft || 0,
              finish_tier_prices: f?.tier_prices || {},
            });
          }}
        >
          <SelectTrigger
            className="input-luxe !text-white"
            data-testid={`measurement-finish-${index}`}
          >
            <SelectValue placeholder="Select finish" />
          </SelectTrigger>
          <SelectContent className="bg-[#1D212C] border-white/10 text-white">
            {finishes.map((f) => (
              <SelectItem
                key={f.id}
                value={f.id}
                data-testid={`finish-option-${f.id}`}
              >
                {f.name} · ₹{f.price_per_sqft}/sqft
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Modular toggle */}
      <label
        className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 mb-3 cursor-pointer transition ${
          m.is_modular
            ? "border-[#C5A880]/60 bg-[#C5A880]/10"
            : "border-white/10 bg-white/[0.02] hover:border-white/20"
        }`}
        data-testid={`measurement-modular-row-${index}`}
      >
        <div className="flex items-center gap-2">
          <Package size={14} className={m.is_modular ? "text-[#C5A880]" : "text-white/50"} />
          <div className="leading-tight">
            <div className={`text-sm font-medium ${m.is_modular ? "text-[#C5A880]" : "text-white/85"}`}>
              Modular
            </div>
            <div className="text-[10px] text-white/40">
              Applies {modMult}× to the finish rate (hardware unchanged)
            </div>
          </div>
        </div>
        <input
          type="checkbox"
          checked={Boolean(m.is_modular)}
          onChange={(e) => onChange({ ...m, is_modular: e.target.checked })}
          className="w-4 h-4 accent-[#C5A880]"
          data-testid={`measurement-modular-${index}`}
        />
      </label>

      <div>
        <label className="label-luxe">Description (optional)</label>
        <input
          className="input-luxe"
          placeholder="e.g. Overhead shutters"
          value={m.description || ""}
          onChange={(e) => onChange({ ...m, description: e.target.value })}
          data-testid={`measurement-desc-${index}`}
        />
      </div>

      <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <Ruler size={14} className="text-[#C5A880]/80" />
          <span className="text-white/60">Area:</span>
          <span className="font-display font-semibold text-white">
            {areaVal.toFixed(2)} sqft
          </span>
          {m.is_modular && (
            <span
              className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full border border-[#C5A880]/50 text-[#C5A880]"
              data-testid={`measurement-modular-badge-${index}`}
            >
              Modular ×{modMult}
            </span>
          )}
        </div>
        <div className="text-sm font-display font-semibold text-[#C5A880]">
          ₹{Math.round(cost).toLocaleString("en-IN")}
        </div>
      </div>
    </div>
  );
}

export default function RoomCard({
  room,
  finishes,
  index,
  onChange,
  onDelete,
  canDelete,
  modularMultiplier,
}) {
  const [open, setOpen] = useState(true);

  const addMeasurement = () => {
    onChange({
      ...room,
      measurements: [
        ...(room.measurements || []),
        {
          id: uid(),
          length_in: "",
          height_in: "",
          length_ft: "",
          height_ft: "",
          finish_id: "",
          finish_name: "",
          finish_price: 0,
          finish_tier_prices: {},
          description: "",
          is_modular: false,
        },
      ],
    });
  };

  const totalSqft = (room.measurements || []).reduce(
    (s, m) => s + sqft(m),
    0
  );

  const measurements = room.measurements || [];
  const roomIncluded = room.is_included !== false;
  const allModular = measurements.length > 0 && measurements.every((m) => m.is_modular);
  const toggleAllModular = () => {
    const target = !allModular;
    onChange({
      ...room,
      measurements: measurements.map((m) => ({ ...m, is_modular: target })),
    });
  };

  return (
    <div
      className={`bg-[#161922] border rounded-2xl overflow-hidden mb-4 transition ${
        roomIncluded ? "border-white/5" : "border-red-500/25"
      }`}
      data-testid={`room-card-${index}`}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-4 flex justify-between items-center bg-white/[0.02] hover:bg-white/[0.04] transition"
        data-testid={`room-toggle-${index}`}
      >
        <div className="flex items-center gap-3 text-left">
          <div className="w-9 h-9 rounded-lg bg-[#C5A880]/10 border border-[#C5A880]/30 flex items-center justify-center">
            <Home size={16} className="text-[#C5A880]" />
          </div>
          <div>
            <div className={`font-display font-semibold text-[15px] ${roomIncluded ? "" : "line-through text-white/40"}`}>
              {room.area_type || "New Room"}
              {room.description ? (
                <span className="text-white/40 font-normal text-sm">
                  {" "}· {room.description}
                </span>
              ) : null}
            </div>
            <div className="text-xs text-white/40 mt-0.5 flex items-center gap-2">
              <span>{(room.measurements || []).length} measurements · {totalSqft.toFixed(1)} sqft</span>
              {!roomIncluded && (
                <span className="text-red-400/80 font-medium">Excluded</span>
              )}
            </div>
          </div>
        </div>
        <ChevronDown
          size={20}
          className={`text-white/40 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="p-4 border-t border-white/5">
          <button
            type="button"
            onClick={() => onChange({ ...room, is_included: !roomIncluded })}
            className={`w-full mb-4 flex items-center justify-between rounded-xl px-3 py-2.5 border transition ${
              roomIncluded
                ? "border-[#C5A880]/30 bg-[#C5A880]/5"
                : "border-red-500/30 bg-red-500/5"
            }`}
            data-testid={`room-include-toggle-${index}`}
          >
            <span className="text-sm font-medium flex items-center gap-2">
              {roomIncluded ? <Eye size={15} className="text-[#C5A880]" /> : <EyeOff size={15} className="text-red-400" />}
              {roomIncluded ? "Included in quotation" : "Excluded from quotation"}
            </span>
            <span className="text-xs text-white/40">tap to {roomIncluded ? "exclude" : "include"} whole room</span>
          </button>
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div>
              <label className="label-luxe">Area Type</label>
              <Select
                value={room.area_type}
                onValueChange={(v) => onChange({ ...room, area_type: v })}
              >
                <SelectTrigger
                  className="input-luxe !text-white"
                  data-testid={`room-type-${index}`}
                >
                  <SelectValue placeholder="Select area type" />
                </SelectTrigger>
                <SelectContent className="bg-[#1D212C] border-white/10 text-white">
                  {AREA_TYPES.map((a) => (
                    <SelectItem key={a} value={a} data-testid={`area-option-${a}`}>
                      {a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-luxe">Description</label>
              <input
                className="input-luxe"
                placeholder="e.g. Master bedroom wardrobe"
                value={room.description}
                onChange={(e) => onChange({ ...room, description: e.target.value })}
                data-testid={`room-desc-${index}`}
              />
            </div>
          </div>

          <div className="glow-divider" />

          <div className="overline text-white/50 mb-3">Reference images</div>
          <RoomImages
            images={room.images || []}
            onChange={(imgs) => onChange({ ...room, images: imgs })}
            testIdPrefix={`room-images-${index}`}
          />

          <div className="glow-divider" />

          <div className="flex items-center justify-between mb-3">
            <div className="overline text-white/50">Measurements</div>
            {measurements.length > 0 && (
              <button
                type="button"
                onClick={toggleAllModular}
                className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border transition ${
                  allModular
                    ? "border-[#C5A880]/60 bg-[#C5A880]/10 text-[#C5A880]"
                    : "border-white/15 text-white/60 hover:border-[#C5A880]/40 hover:text-[#C5A880]"
                }`}
                data-testid={`room-modular-all-${index}`}
                title="Apply the modular multiplier to every measurement in this room"
              >
                <Package size={12} />
                {allModular ? "Unmark all modular" : "Mark all modular"}
              </button>
            )}
          </div>

          {(room.measurements || []).length === 0 && (
            <div className="text-center py-6 text-white/30 text-sm">
              No measurements yet. Tap + below to add.
            </div>
          )}

          {(room.measurements || []).map((m, mi) => (
            <MeasurementBlock
              key={m.id || `m-${mi}`}
              m={m}
              finishes={finishes}
              index={mi}
              modularMultiplier={modularMultiplier}
              onChange={(nm) => {
                const arr = [...room.measurements];
                arr[mi] = nm;
                onChange({ ...room, measurements: arr });
              }}
              onDelete={() => {
                const arr = [...room.measurements];
                arr.splice(mi, 1);
                onChange({ ...room, measurements: arr });
              }}
            />
          ))}

          <button
            type="button"
            onClick={addMeasurement}
            className="w-full mt-2 h-12 rounded-xl border border-dashed border-[#C5A880]/40 text-[#C5A880] hover:bg-[#C5A880]/5 flex items-center justify-center gap-2 font-medium transition"
            data-testid={`add-measurement-${index}`}
          >
            <Plus size={16} />
            Add Measurement
          </button>

          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="mt-3 text-xs text-red-400/70 hover:text-red-400 flex items-center gap-1.5 mx-auto"
              data-testid={`room-delete-${index}`}
            >
              <Trash2 size={13} />
              Delete this room
            </button>
          )}
        </div>
      )}
    </div>
  );
}
